import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { HelmetProvider } from 'react-helmet-async';
import * as Sentry from '@sentry/react';
import { Toaster } from './components/ui/sonner';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { MaintenanceProvider } from './components/MaintenanceGuard';
import './App.css';

// Sentry Error Fallback Component
const SentryErrorFallback = ({ error, resetError }) => (
  <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-slate-900 to-indigo-950">
    <div className="text-center p-8 max-w-md">
      <div className="text-6xl mb-4">🦑</div>
      <h1 className="text-2xl font-bold text-white mb-4">Something went wrong</h1>
      <p className="text-gray-400 mb-6">
        We've been notified about this error and are working to fix it.
      </p>
      <button
        onClick={() => {
          resetError();
          window.location.href = '/';
        }}
        className="px-6 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
      >
        Return Home
      </button>
    </div>
  </div>
);

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-slate-900 to-indigo-950">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400 mx-auto mb-4"></div>
      <p className="text-cyan-400">Loading...</p>
    </div>
  </div>
);

// Lazy loaded pages - Critical pages loaded normally, others lazy loaded
import Home from './pages/Home';
import Login from './pages/Login';

// Lazy load non-critical pages
const About = lazy(() => import('./pages/About'));
const Courses = lazy(() => import('./pages/Courses'));
const CourseDetail = lazy(() => import('./pages/CourseDetail'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Certificates = lazy(() => import('./pages/Certificates'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const InstructorDashboard = lazy(() => import('./pages/InstructorDashboard'));
const Settings = lazy(() => import('./pages/Settings'));
const Support = lazy(() => import('./pages/Support'));
const Profile = lazy(() => import('./pages/Profile'));
const Orientation = lazy(() => import('./pages/Orientation'));
const SquidTour = lazy(() => import('./pages/SquidTour'));
const SquidMember = lazy(() => import('./pages/SquidMember'));
const CareerRoadmap = lazy(() => import('./pages/CareerRoadmap'));
const CareerPathDetail = lazy(() => import('./pages/CareerPathDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Contact = lazy(() => import('./pages/Contact'));
const Mentorship = lazy(() => import('./pages/Mentorship'));
// AdminPanel removed - migrated to SQUIDBOARD
const DevPanel = lazy(() => import('./pages/DevPanel'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const Careers = lazy(() => import('./pages/Careers'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const ClassroomList = lazy(() => import('./pages/Classroom/ClassroomList'));
const ClassroomCourses = lazy(() => import('./pages/Classroom/ClassroomCourses'));
const ClassroomPage = lazy(() => import('./pages/Classroom/ClassroomPage'));
const CertificatePage = lazy(() => import('./pages/Classroom/CertificatePage'));
const InstructorPanel = lazy(() => import('./pages/Classroom/InstructorPanel'));
const CourseManager = lazy(() => import('./pages/Classroom/CourseManager'));
const CourseEditor = lazy(() => import('./pages/Classroom/CourseEditor'));
const MyEvents = lazy(() => import('./pages/Classroom/MyEvents'));
const MyMessages = lazy(() => import('./pages/Classroom/MyMessages'));
const CommunityBoard = lazy(() => import('./pages/Classroom/CommunityBoard'));
const CommunityTopic = lazy(() => import('./pages/Classroom/CommunityTopic'));
const LMSPaymentSuccess = lazy(() => import('./pages/Classroom/PaymentSuccess'));
const RegistrationPortal = lazy(() => import('./pages/Classroom/Registration').then(m => ({ default: m.RegistrationPortal })));
const CohortCoursePage = lazy(() => import('./pages/Classroom/Registration').then(m => ({ default: m.CohortCoursePage })));
const Partnership = lazy(() => import('./pages/Partnership'));
const Donate = lazy(() => import('./pages/Donate'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const Terms = lazy(() => import('./pages/Terms'));
const CertPrep = lazy(() => import('./pages/CertPrep'));
const CertPrepTest = lazy(() => import('./pages/CertPrepTest'));
const CertPrepResult = lazy(() => import('./pages/CertPrepResult'));
const StudentReviews = lazy(() => import('./pages/StudentReviews'));
const ADChallengeApplication = lazy(() => import('./pages/ADChallengeApplication'));
const Jobs = lazy(() => import('./pages/Jobs'));
const Bootcamps = lazy(() => import('./pages/Bootcamps'));
const BootcampDetail = lazy(() => import('./pages/BootcampDetail'));
const BootcampLearn = lazy(() => import('./pages/BootcampLearn'));
const ADBootcampCoursePage = lazy(() => import('./pages/ADBootcampCoursePage'));
const StudentDashboard = lazy(() => import('./pages/StudentDashboard'));
const MyCalendar = lazy(() => import('./pages/MyCalendar'));
const UserManagement = lazy(() => import('./pages/Classroom/UserManagement'));
const BlackboardCalendar = lazy(() => import('./pages/Classroom/MyCalendar'));
const SecurityMonitor = lazy(() => import('./pages/Classroom/SecurityMonitor'));
const FinanceDashboard = lazy(() => import('./pages/Classroom/FinanceDashboard'));
const ITSupport = lazy(() => import('./pages/Classroom/ITSupport'));
const BootcampInstructorDashboard = lazy(() => import('./pages/BootcampInstructorDashboard'));
const BootcampAssessment = lazy(() => import('./components/BootcampAssessment'));
const AssessmentResultsPage = lazy(() => import('./pages/AssessmentResultsPage'));
const CapstoneSubmission = lazy(() => import('./components/CapstoneSubmission'));
const BootcampCertificate = lazy(() => import('./pages/BootcampCertificate'));
const CertificateVerify = lazy(() => import('./pages/CertificateVerify'));
const EmailPreviewAdmin = lazy(() => import('./pages/EmailPreviewAdmin'));
const LabGallery = lazy(() => import('./pages/LabGallery'));
const JobDetail = lazy(() => import('./pages/JobDetail'));
const VoucherRequest = lazy(() => import('./pages/VoucherRequest'));
const Schedule = lazy(() => import('./pages/Schedule'));
const CareerQuiz = lazy(() => import('./pages/CareerQuiz'));
const ResumeBuilder = lazy(() => import('./pages/ResumeBuilder'));
const Downloads = lazy(() => import('./pages/Downloads'));
const Orders = lazy(() => import('./pages/Orders'));
const Events = lazy(() => import('./pages/Events'));
const EventDetail = lazy(() => import('./pages/EventDetail'));
const BookingLanding = lazy(() => import('./pages/BookingLanding'));
const BookingPage = lazy(() => import('./pages/BookingPage'));
const ConsultingBooking = lazy(() => import('./pages/ConsultingBooking'));
const ConsultantProfile = lazy(() => import('./pages/ConsultantProfile'));
const ConsultingSuccess = lazy(() => import('./pages/ConsultingSuccess'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const CreateEventTypePage = lazy(() => import('./pages/CreateEventTypePage'));
const SquidCloud = lazy(() => import('./pages/SquidCloud'));
const VMConsole = lazy(() => import('./pages/VMConsole'));
// EmbeddedConsole removed - functionality merged into VMConsole
const BuyTokens = lazy(() => import('./pages/BuyTokens'));
const BuyCoins = lazy(() => import('./pages/BuyCoins'));
const Internships = lazy(() => import('./pages/Internships'));
const InternshipDetail = lazy(() => import('./pages/InternshipDetail'));
const ConfirmEmail = lazy(() => import('./pages/ConfirmEmail'));
const ResendConfirmation = lazy(() => import('./pages/ResendConfirmation'));
const Timeline = lazy(() => import('./pages/Timeline'));
const Privacy = lazy(() => import('./pages/Privacy'));
const ResumeLab = lazy(() => import('./pages/ResumeLab'));
const ITToolkit = lazy(() => import('./pages/ITToolkit'));
const StudyAssistant = lazy(() => import('./pages/StudyAssistant'));
const Flashcards = lazy(() => import('./pages/Flashcards'));
const EntranceExams = lazy(() => import('./pages/EntranceExams'));
const InterviewPrep = lazy(() => import('./pages/InterviewPrep'));
const CaptureTheSquid = lazy(() => import('./pages/CaptureTheSquid'));
const CTSPage = lazy(() => import('./pages/CTSPage'));
const SquidsLife = lazy(() => import('./pages/SquidsLife'));
const SquidTrench = lazy(() => import('./pages/SquidTrench'));
const SquidFeud = lazy(() => import('./pages/SquidFeud'));
const SocialMediaDashboard = lazy(() => import('./pages/SocialMediaDashboard'));
const SocialMediaManager = lazy(() => import('./pages/Classroom/SocialMediaManager'));
const Labs = lazy(() => import('./pages/Labs'));
const Shop = lazy(() => import('./pages/Shop'));
const Library = lazy(() => import('./pages/Library'));
const SquidCookies = lazy(() => import('./pages/SquidCookies'));
const SquidPrivacy = lazy(() => import('./pages/SquidPrivacy'));
const PWYCPolicy = lazy(() => import('./pages/PWYCPolicy'));
const Squidpedia = lazy(() => import('./pages/Squidpedia'));
const Worlds = lazy(() => import('./pages/Worlds'));
const Squidverse = lazy(() => import('./pages/Squidverse'));
const SquidSeas = lazy(() => import('./pages/SquidSeas'));
const SquidWorld = lazy(() => import('./pages/SquidWorld'));
const SquidLand = lazy(() => import('./pages/SquidLand'));
const SquidBug = lazy(() => import('./pages/SquidBug'));
const OceanDetail = lazy(() => import('./pages/OceanDetail'));
const WorldDetail = lazy(() => import('./pages/WorldDetail'));
const SeaDetail = lazy(() => import('./pages/SeaDetail'));
const MentorDirectory = lazy(() => import('./pages/MentorDirectory'));
const MentorProfile = lazy(() => import('./pages/MentorProfile'));
const MentorApplication = lazy(() => import('./pages/MentorApplication'));
const MentorApplicationSuccess = lazy(() => import('./pages/MentorApplicationSuccess'));
const MentorshipDashboard = lazy(() => import('./pages/MentorshipDashboard'));
const MentorMatchesPage = lazy(() => import('./pages/MentorMatches'));
const MenteeIntakeForm = lazy(() => import('./pages/MenteeIntakeForm'));
const MentorshipTiers = lazy(() => import('./pages/MentorshipTiers'));
const MentorDashboard = lazy(() => import('./pages/MentorDashboard'));
const MentorshipUserDashboard = lazy(() => import('./pages/MentorshipUserDashboard'));
const MentorSessions = lazy(() => import('./pages/MentorSessions'));
const MentorEarnings = lazy(() => import('./pages/MentorEarnings'));
const Referrals = lazy(() => import('./pages/Referrals'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Games
const SquidopolyPage = lazy(() => import('./games/squidopoly/SquidopolyPage'));

// SIEM Security Dashboard
const SiemDashboard = lazy(() => import('./pages/SiemDashboard'));

// SQUIDBOARD - Project Management
const SquidboardPage = lazy(() => import('./pages/squidboard/SquidboardPage'));
const EmployeeLifecycle = lazy(() => import('./pages/squidboard/EmployeeLifecycle'));
const AnsibleDashboard = lazy(() => import('./pages/squidboard/AnsibleDashboard'));
const SquidCloudAdmin = lazy(() => import('./pages/squidboard/SquidCloudAdmin'));
const AdminReviews = lazy(() => import('./pages/Admin/AdminReviews'));
const AdminADChallenge = lazy(() => import('./pages/Admin/AdminADChallenge'));

// Creator Portal - Instructor course creation
const TeachPage = lazy(() => import('./pages/TeachPage'));
const InstructorApplyPage = lazy(() => import('./pages/InstructorApplyPage'));
const CreatorDashboard = lazy(() => import('./pages/creator/CreatorDashboard'));
const CourseBuilder = lazy(() => import('./pages/creator/CourseBuilder'));
const CreatorEarnings = lazy(() => import('./pages/creator/CreatorEarnings'));

// Non-lazy loaded components (used everywhere or critical)
import AuthCallback from './components/AuthCallback';
import ProtectedRoute from './components/ProtectedRoute';
import PublicPageGuard from './components/PublicPageGuard';
import GoogleAnalytics from './components/GoogleAnalytics';
import DiscordBottomBar from './components/DiscordBottomBar';
import CookieConsent from './components/CookieConsent';
import MaintenanceBanner from './components/MaintenanceBanner';
import WorldRouter from './components/WorldRouter';
import SquidTourGuard from './components/SquidTourGuard';
import NDAGuard from './components/NDAGuard';
import PWYCBanner from './components/PWYCBanner';
import ProfileCompletionGuard from './components/ProfileCompletionGuard';
import Squidy from './components/Squidy';
import axios from 'axios';
import { getAuthHeaders } from './lib/authHeaders';

import { API } from './config/api';

// ScrollToTop component - scrolls to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

function AppRouter() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  
  // Fetch user on mount
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${API}/auth/me`, { 
          withCredentials: true,
          headers 
        });
        setUser(response.data);
      } catch (error) {
        setUser(null);
      }
    };
    fetchUser();
  }, [location.pathname]);
  
  // Check for session_id in URL fragment
  if (location.hash && location.hash.includes('session_id=')) {
    console.log('OAuth callback detected, hash:', location.hash);
    return <AuthCallback />;
  }
  
  // Pages where Discord bottom bar and Squidy should NOT appear
  const hideDiscordPaths = ['/login', '/register', '/admin', '/squidboard', '/blackboard'];
  // Only show Discord bar to logged-in users AND not on hidden paths
  const shouldShowDiscord = user && !hideDiscordPaths.some(path => location.pathname.startsWith(path));
  const shouldShowSquidy = user && !hideDiscordPaths.some(path => location.pathname.startsWith(path));
  
  // Check if we're on SQUIDBOARD for conditional padding
  const isSquidboard = location.pathname.startsWith('/squidboard');
  
  return (
    <PublicPageGuard user={user}>
      <SquidTourGuard user={user}>
        <NDAGuard>
          <PWYCBanner />
          <ScrollToTop />
          <div className={shouldShowDiscord ? 'pb-16' : ''}> {/* Add padding bottom for Discord bar only when shown */}
            <GoogleAnalytics />
            <Suspense fallback={<PageLoader />}>
            <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/about" element={<About />} />
          <Route path="/courses" element={<Courses />} />
          <Route path="/courses/:courseId" element={<CourseDetail />} />
          <Route path="/mentorship" element={<Mentorship />} />
          <Route path="/mentors" element={<Navigate to="/mentorship" replace />} />
          <Route path="/mentors/matches" element={<MentorMatchesPage />} />
          <Route path="/mentorship/matches" element={<MentorMatchesPage />} />
          <Route path="/mentors/:mentorId" element={<MentorProfile />} />
          <Route path="/mentor/:mentorId" element={<MentorProfile />} />
          <Route path="/mentor-directory" element={<MentorDirectory />} />
          <Route path="/mentorship/apply" element={
            <ProtectedRoute>
              <MentorApplication />
            </ProtectedRoute>
          } />
          <Route path="/mentorship/application-success" element={
            <ProtectedRoute>
              <MentorApplicationSuccess />
            </ProtectedRoute>
          } />
          <Route path="/mentorship/dashboard" element={
            <ProtectedRoute>
              <MentorshipDashboard />
            </ProtectedRoute>
          } />
          <Route path="/mentor-dashboard" element={
            <ProtectedRoute>
              <MentorDashboard />
            </ProtectedRoute>
          } />
          <Route path="/mentorship/my-dashboard" element={
            <ProtectedRoute>
              <MentorshipUserDashboard />
            </ProtectedRoute>
          } />
          <Route path="/mentorship/sessions" element={
            <ProtectedRoute>
              <MentorSessions />
            </ProtectedRoute>
          } />
          <Route path="/mentorship/earnings" element={
            <ProtectedRoute>
              <MentorEarnings />
            </ProtectedRoute>
          } />
          <Route path="/mentorship/intake" element={<MenteeIntakeForm />} />
          <Route path="/mentee-intake" element={<Navigate to="/mentorship/intake" replace />} />
          <Route path="/mentorship/tiers" element={<MentorshipTiers />} />
          <Route path="/reviews" element={<StudentReviews />} />
          <Route path="/challenges/ad-challenge/apply" element={<ADChallengeApplication />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/help" element={<Support />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/careers/:jobId" element={<JobDetail />} />
          <Route path="/partnership" element={<Partnership />} />
          <Route path="/donate" element={<Donate />} />
          <Route path="/donate/success" element={<Donate />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/entrance-exams" element={<EntranceExams />} />
          <Route path="/labs" element={
            <ProtectedRoute>
              <Labs />
            </ProtectedRoute>
          } />
          <Route path="/library" element={
            <ProtectedRoute>
              <Library />
            </ProtectedRoute>
          } />
          <Route path="/squid-drive" element={
            <ProtectedRoute>
              <Library />
            </ProtectedRoute>
          } />
          <Route path="/squidpedia" element={
            <ProtectedRoute>
              <Squidpedia />
            </ProtectedRoute>
          } />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/portfolio" element={<Portfolio />} />
          <Route path="/blackboard" element={<ProtectedRoute><ClassroomList /></ProtectedRoute>} />
          <Route path="/blackboard/community" element={<ProtectedRoute><CommunityBoard /></ProtectedRoute>} />
          <Route path="/blackboard/community/:topicId" element={<ProtectedRoute><CommunityTopic /></ProtectedRoute>} />
          <Route path="/blackboard/courses" element={<ProtectedRoute><ClassroomCourses /></ProtectedRoute>} />
          <Route path="/blackboard/events" element={<ProtectedRoute><MyEvents /></ProtectedRoute>} />
          <Route path="/blackboard/messages" element={<ProtectedRoute><MyMessages /></ProtectedRoute>} />
          <Route path="/blackboard/certificate/:certificateId" element={<ProtectedRoute><CertificatePage /></ProtectedRoute>} />
          <Route path="/blackboard/instructor" element={<ProtectedRoute><InstructorDashboard /></ProtectedRoute>} />
          <Route path="/blackboard/i-panel" element={<ProtectedRoute><InstructorPanel /></ProtectedRoute>} />
          <Route path="/blackboard/manage-courses" element={<ProtectedRoute><CourseManager /></ProtectedRoute>} />
          <Route path="/blackboard/manage-courses/:courseId" element={<ProtectedRoute><CourseEditor /></ProtectedRoute>} />
          <Route path="/blackboard/admin/users" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
          <Route path="/blackboard/admin/security" element={<ProtectedRoute><SecurityMonitor /></ProtectedRoute>} />
          <Route path="/blackboard/admin/finance" element={<ProtectedRoute><FinanceDashboard /></ProtectedRoute>} />
          <Route path="/blackboard/admin/support" element={<ProtectedRoute><ITSupport /></ProtectedRoute>} />
          <Route path="/blackboard/admin/social-media" element={<ProtectedRoute><SocialMediaManager /></ProtectedRoute>} />
          <Route path="/blackboard/my-calendar" element={<ProtectedRoute><BlackboardCalendar /></ProtectedRoute>} />
          <Route path="/blackboard/payment-success" element={<ProtectedRoute><LMSPaymentSuccess /></ProtectedRoute>} />
          <Route path="/registration" element={<RegistrationPortal />} />
          <Route path="/registration/:cohortId" element={<CohortCoursePage />} />
          <Route path="/blackboard/:courseId" element={<ProtectedRoute><ClassroomPage /></ProtectedRoute>} />
          <Route path="/jermain" element={<Portfolio />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/squidcookies" element={<SquidCookies />} />
          <Route path="/squidprivacy" element={<SquidPrivacy />} />
          <Route path="/pwyc-policy" element={<PWYCPolicy />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signin" element={<Navigate to="/login" replace />} />
          <Route path="/register" element={<Login />} />
          <Route path="/signup" element={<Navigate to="/register" replace />} />
          <Route path="/exams" element={<Navigate to="/cert-prep" replace />} />
          <Route path="/confirm-email" element={<ConfirmEmail />} />
          <Route path="/resend-confirmation" element={<ResendConfirmation />} />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/calendar" element={
            <ProtectedRoute>
              <MyCalendar />
            </ProtectedRoute>
          } />
          <Route path="/referrals" element={
            <ProtectedRoute>
              <Referrals />
            </ProtectedRoute>
          } />
          <Route path="/certificates" element={
            <ProtectedRoute>
              <Certificates />
            </ProtectedRoute>
          } />
          <Route path="/wishlist" element={
            <ProtectedRoute>
              <Wishlist />
            </ProtectedRoute>
          } />
          <Route path="/instructor/dashboard" element={
            <ProtectedRoute>
            <InstructorDashboard />
          </ProtectedRoute>
        } />
        {/* Helpdesk removed - redirect to /support */}
        <Route path="/helpdesk" element={<Navigate to="/help" replace />} />
        <Route path="/support" element={<Navigate to="/help" replace />} />
        <Route path="/resume-lab" element={
          <ProtectedRoute>
            <ResumeLab />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } />
        <Route path="/orientation" element={
          <ProtectedRoute>
            <Orientation />
          </ProtectedRoute>
        } />
        <Route path="/roadmap" element={
          <ProtectedRoute>
            <CareerRoadmap />
          </ProtectedRoute>
        } />
        <Route path="/career/:career" element={<CareerPathDetail />} />
        {/* Redirect old hands-on-labs routes to /labs */}
        <Route path="/hands-on-labs/:courseId" element={<Navigate to="/labs" replace />} />
        <Route path="/hands-on-labs" element={<Navigate to="/labs" replace />} />
        {/* Cart is accessible to all users - guests can checkout and create account after payment */}
        <Route path="/cart" element={<Cart />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/squidbug" element={<SquidBug />} />
        {/* /admin route removed - all admin functionality migrated to SQUIDBOARD */}
        <Route path="/admin/devpanel" element={
          <ProtectedRoute requireAdmin>
            <DevPanel />
          </ProtectedRoute>
        } />
        <Route path="/admin/social-media" element={
          <ProtectedRoute requireAdmin>
            <SocialMediaDashboard />
          </ProtectedRoute>
        } />
        {/* PaymentSuccess is NOT protected - must handle Stripe redirect with session_id params */}
        {/* The page handles both logged-in and logged-out users gracefully */}
        <Route path="/payment/success" element={<PaymentSuccess />} />
        {/* CertPrep is public - handles auth internally for free trials */}
        <Route path="/cert-prep" element={<CertPrep />} />
        <Route path="/certifications" element={<Navigate to="/cert-prep" replace />} />
        <Route path="/cert-prep/test/:certId" element={<CertPrepTest />} />
        <Route path="/cert-prep/result/:resultId" element={
          <ProtectedRoute>
            <CertPrepResult />
          </ProtectedRoute>
        } />
        <Route path="/jobs" element={
          <ProtectedRoute>
            <Jobs />
          </ProtectedRoute>
        } />
        <Route path="/vouchers" element={
          <ProtectedRoute>
            <VoucherRequest />
          </ProtectedRoute>
        } />
        <Route path="/voucher-request" element={
          <ProtectedRoute>
            <VoucherRequest />
          </ProtectedRoute>
        } />
        <Route path="/schedule" element={<Schedule />} />
        <Route path="/career-quiz" element={<CareerQuiz />} />
        <Route path="/resume-builder" element={
          <ProtectedRoute>
            <ResumeBuilder />
          </ProtectedRoute>
        } />
        <Route path="/downloads" element={
          <ProtectedRoute>
            <Downloads />
          </ProtectedRoute>
        } />
        <Route path="/orders" element={
          <ProtectedRoute>
            <Orders />
          </ProtectedRoute>
        } />
        
        {/* Events Routes - Public pages */}
        <Route path="/events" element={<Events />} />
        <Route path="/events/:slug" element={<EventDetail />} />
        
        {/* Booking Routes - Calendly-like scheduling */}
        <Route path="/booking" element={<BookingLanding />} />
        <Route path="/booking/:slug" element={<BookingPage />} />
        <Route path="/book/:slug" element={<BookingPage />} />
        <Route path="/calendar/create-event" element={<CreateEventTypePage />} />
        
        {/* Consulting Booking */}
        <Route path="/consulting" element={<ConsultingBooking />} />
        <Route path="/consulting/success" element={<ConsultingSuccess />} />
        <Route path="/consulting/expert/:consultantId" element={<ConsultantProfile />} />
        
        {/* Calendar Page - Full calendar view */}
        <Route path="/calendar" element={<CalendarPage />} />
        
        {/* SquidCloud VM Rental */}
        <Route path="/squidcloud" element={<SquidCloud />} />
        <Route path="/vm-console" element={<VMConsole />} />
        <Route path="/vm-console/:rentalId" element={<VMConsole />} />
        {/* VM Console */}
        <Route path="/console/:rentalId" element={<Navigate to="/vm-console" replace />} />
        
        <Route path="/buy-tokens" element={<BuyCoins />} />
        <Route path="/buy-coins" element={<BuyCoins />} />
        <Route path="/internships" element={<Internships />} />
        <Route path="/internships/:internshipId" element={<InternshipDetail />} />
        <Route path="/timeline" element={<Timeline />} />
        {/* Generic bootcamp routes - more specific paths first */}
        <Route path="/bootcamps" element={<Navigate to="/courses?tab=bootcamps" replace />} />
        <Route path="/bootcamps/:bootcampId/dashboard" element={
          <ProtectedRoute>
            <StudentDashboard />
          </ProtectedRoute>
        } />
        <Route path="/bootcamps/:bootcampId/instructor" element={
          <ProtectedRoute requireAdmin>
            <BootcampInstructorDashboard />
          </ProtectedRoute>
        } />
        <Route path="/bootcamps/:bootcampId/learn" element={
          <ProtectedRoute>
            <BootcampLearn />
          </ProtectedRoute>
        } />
        <Route path="/bootcamp/:bootcampId/learn" element={
          <ProtectedRoute>
            <BootcampLearn />
          </ProtectedRoute>
        } />
        {/* AD Bootcamp course page - specific route after generic /learn */}
        <Route path="/bootcamps/ad-basics-bootcamp-001" element={<ADBootcampCoursePage />} />
        <Route path="/bootcamp/ad-basics-bootcamp-001" element={<ADBootcampCoursePage />} />
        <Route path="/bootcamps/:bootcampId" element={<BootcampDetail />} />
        <Route path="/bootcamp/:bootcampId" element={<BootcampDetail />} />
        <Route path="/bootcamps/:bootcampId/assessments/:assessmentId" element={
          <ProtectedRoute>
            <BootcampAssessment />
          </ProtectedRoute>
        } />
        <Route path="/bootcamps/:bootcampId/assessments/:assessmentId/results" element={
          <ProtectedRoute>
            <AssessmentResultsPage />
          </ProtectedRoute>
        } />
        <Route path="/bootcamps/:bootcampId/capstone" element={
          <ProtectedRoute>
            <CapstoneSubmission />
          </ProtectedRoute>
        } />
        <Route path="/bootcamps/:bootcampId/certificate/:certificateId" element={
          <BootcampCertificate />
        } />
        <Route path="/tools" element={
          <ProtectedRoute>
            <ITToolkit />
          </ProtectedRoute>
        } />
        <Route path="/study" element={
          <ProtectedRoute>
            <StudyAssistant />
          </ProtectedRoute>
        } />
        <Route path="/flashcards" element={<Flashcards />} />
        <Route path="/interview-prep" element={
          <ProtectedRoute>
            <InterviewPrep />
          </ProtectedRoute>
        } />
        <Route path="/capture-the-squid" element={
          <ProtectedRoute>
            <CaptureTheSquid />
          </ProtectedRoute>
        } />
        {/* CTS - Capture the Squid Level System (Public) */}
        <Route path="/cts" element={<CTSPage />} />
        {/* Squidopoly Game */}
        <Route path="/squidopoly" element={
          <ProtectedRoute>
            <SquidopolyPage />
          </ProtectedRoute>
        } />
        {/* SQUIDBOARD - Project Management (access-gated internally) */}
        <Route path="/squidboard" element={
          <ProtectedRoute>
            <SquidboardPage />
          </ProtectedRoute>
        } />
        {/* SQUIDBOARD - Employee Lifecycle Management */}
        <Route path="/squidboard/lifecycle" element={
          <ProtectedRoute>
            <EmployeeLifecycle />
          </ProtectedRoute>
        } />
        {/* SQUIDBOARD - Ansible/AWX Dashboard */}
        <Route path="/squidboard/ansible" element={
          <ProtectedRoute>
            <AnsibleDashboard />
          </ProtectedRoute>
        } />
        {/* SQUIDBOARD - SquidCloud Admin */}
        <Route path="/squidboard/squidcloud" element={
          <ProtectedRoute requiredRole="admin">
            <SquidCloudAdmin />
          </ProtectedRoute>
        } />
        {/* SQUIDBOARD - Review Management */}
        <Route path="/squidboard/reviews" element={
          <ProtectedRoute requiredRole="admin">
            <AdminReviews />
          </ProtectedRoute>
        } />
        {/* SQUIDBOARD - AD Challenge Applications */}
        <Route path="/squidboard/ad-challenge" element={
          <ProtectedRoute requiredRole="admin">
            <AdminADChallenge />
          </ProtectedRoute>
        } />
        <Route path="/squidboard/siem" element={
          <ProtectedRoute requiredRole="admin">
            <SiemDashboard />
          </ProtectedRoute>
        } />
        {/* Creator Portal - Teach on Squidemy */}
        <Route path="/teach" element={<TeachPage />} />
        <Route path="/teach/apply" element={
          <ProtectedRoute>
            <InstructorApplyPage />
          </ProtectedRoute>
        } />
        <Route path="/creator" element={
          <ProtectedRoute>
            <CreatorDashboard />
          </ProtectedRoute>
        } />
        <Route path="/creator/courses" element={
          <ProtectedRoute>
            <CreatorDashboard />
          </ProtectedRoute>
        } />
        <Route path="/creator/courses/new" element={
          <ProtectedRoute>
            <CourseBuilder />
          </ProtectedRoute>
        } />
        <Route path="/creator/courses/:courseId" element={
          <ProtectedRoute>
            <CourseBuilder />
          </ProtectedRoute>
        } />
        <Route path="/creator/earnings" element={
          <ProtectedRoute>
            <CreatorEarnings />
          </ProtectedRoute>
        } />
        {/* Legacy routes - redirect to new /worlds/* structure */}
        <Route path="/squid-trench" element={<Navigate to="/worlds/squidtrench" replace />} />
        <Route path="/mariana-trench" element={<Navigate to="/worlds/squidtrench" replace />} />
        <Route path="/squids-life" element={<Navigate to="/worlds/squidslife" replace />} />
        <Route path="/squids-life/:labId" element={<Navigate to="/worlds/squidslife" replace />} />
        <Route path="/squidverse" element={<Navigate to="/worlds/squidverse" replace />} />
        <Route path="/squidtour" element={<Navigate to="/worlds/squidtour" replace />} />
        {/* Squid Feud Game */}
        <Route path="/squid-feud" element={<SquidFeud />} />
        {/* SquidMember Subscription - Admin Only (hidden for now) */}
        <Route path="/squidmember" element={
          <ProtectedRoute requireAdmin={true}>
            <SquidMember />
          </ProtectedRoute>
        } />
        <Route path="/squidmember/success" element={<PaymentSuccess />} />
        {/* Squidverse World System Routes */}
        <Route path="/worlds" element={
          <ProtectedRoute>
            <Worlds />
          </ProtectedRoute>
        } />
        {/* SquidTour - Orientation (specific route before dynamic) */}
        <Route path="/worlds/squidtour" element={
          <ProtectedRoute>
            <SquidTour />
          </ProtectedRoute>
        } />
        {/* SquidLand - Foundation Zone with Mario-style map */}
        <Route path="/worlds/squidland" element={
          <ProtectedRoute>
            <SquidLand />
          </ProtectedRoute>
        } />
        {/* SquidSeas - Competence Realm with Networking Gauntlet */}
        <Route path="/worlds/squidseas" element={
          <ProtectedRoute>
            <SquidSeas />
          </ProtectedRoute>
        } />
        {/* SquidWorld - Leadership World with Final Challenge */}
        <Route path="/worlds/squidworld" element={
          <ProtectedRoute>
            <SquidWorld />
          </ProtectedRoute>
        } />
        <Route path="/worlds/:worldId" element={
          <ProtectedRoute>
            <WorldRouter />
          </ProtectedRoute>
        } />
        {/* Legacy squidverse routes */}
        <Route path="/squidverse/:oceanId" element={
          <ProtectedRoute>
            <OceanDetail />
          </ProtectedRoute>
        } />
        <Route path="/squidverse/:oceanId/:seaId" element={
          <ProtectedRoute>
            <SeaDetail />
          </ProtectedRoute>
        } />
        <Route path="/squidverse/sea/:seaId" element={
          <ProtectedRoute>
            <SeaDetail />
          </ProtectedRoute>
        } />
        {/* Public Certificate Verification */}
        <Route path="/verify/:certificateId" element={<CertificateVerify />} />
        {/* Admin Email Preview */}
        <Route path="/admin/email-preview" element={
          <ProtectedRoute>
            <EmailPreviewAdmin />
          </ProtectedRoute>
        } />
        {/* Lab Gallery */}
        <Route path="/bootcamps/:bootcampId/gallery" element={
          <ProtectedRoute>
            <LabGallery />
          </ProtectedRoute>
        } />
        {/* 404 Catch-all route - must be last */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      <Toaster position="top-center" />
      {/* Discord Bottom Bar - shows on all pages except login/admin/squidboard */}
      {shouldShowDiscord && <DiscordBottomBar user={user} />}
      {/* Squidy - Navigation Assistant for logged-in users (hidden on squidboard) */}
      {shouldShowSquidy && <Squidy />}
      {/* Cookie Consent Banner */}
      <CookieConsent />
      </div>
        </NDAGuard>
      </SquidTourGuard>
    </PublicPageGuard>
  );
}

function App() {
  // Check if we're on localhost or preview
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const isPreview = window.location.hostname.includes('preview.emergentagent.com');

  return (
    <Sentry.ErrorBoundary fallback={SentryErrorFallback} showDialog>
      <HelmetProvider>
        <ThemeProvider>
          <AuthProvider>
            <BrowserRouter>
              <MaintenanceBanner />
              <MaintenanceProvider>
                <AppRouter />
              </MaintenanceProvider>
            </BrowserRouter>
          </AuthProvider>
        </ThemeProvider>
      </HelmetProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
